import { useMemo, useState } from "react";

import { t } from "i18next";

import { SwipeableDrawer, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ModalWrapper from "@/src/components/molecules/ModalWrapper";

import {
  useDeleteNote,
  useNoteHistory,
  usePostNote
} from "../../../../queries/dealer/crudNotes";
import { notificationAtom } from "../../../../state/notificationAtom";
import { DealerAuctionDTO, NoteDTO } from "../../../../types/types";
import { groupHistoryByAuction } from "../../../../utils/noteUtils";
import NoteModalContent from "./NoteModalContent";

import { useSetAtom } from "jotai";

interface AddNoteModalProps {
  auction: DealerAuctionDTO;
  notes?: NoteDTO[];
  isOpen: boolean;
  onClose: () => void;
}

const NoteModal = (props: AddNoteModalProps) => {
  const { auction, notes, isOpen, onClose } = props;
  const auctionId = auction.id;
  const currentMileage = auction.auctionItem.auctionItemDetails?.mileage;
  const mileageUnit = auction.auctionItem.auctionItemDetails?.mileageUnit;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [noteText, setNoteText] = useState("");
  const setNotification = useSetAtom(notificationAtom);
  const { isPending: isSending, mutateAsync: postNote } = usePostNote();
  const {
    data: noteHistory,
    isError: isNoteHistoryError,
    refetch: refetchNoteHistory
  } = useNoteHistory(auctionId, isOpen);
  const { mutateAsync: deleteNote } = useDeleteNote();
  const historyGroups = useMemo(
    () => groupHistoryByAuction(noteHistory),
    [noteHistory]
  );

  const handleClose = () => {
    setNoteText("");
    onClose();
  };

  const handleAddNote = async () => {
    const content = noteText.trim();
    if (!content) return;

    try {
      await postNote({ auctionId, content: { content } });
      setNotification({
        text: t("notifications.addNoteSuccess"),
        variant: "success"
      });
      setNoteText("");
    } catch (e) {
      setNotification({
        text: t("notifications.addNoteFailure"),
        variant: "error"
      });
    }
  };

  const handleDeleteNote = async (note: NoteDTO) => {
    try {
      await deleteNote({ auctionId, noteId: note.id });
      setNotification({
        text: t("notifications.deleteNoteSuccess"),
        variant: "success"
      });
    } catch (e) {
      setNotification({
        text: t("notifications.deleteNoteFailure"),
        variant: "error"
      });
    }
  };

  const regNo = auction.auctionItem.vehicle?.regNo?.toUpperCase();

  const contentProps = {
    regNo,
    notes,
    historyGroups,
    currentMileage,
    mileageUnit,
    isNoteHistoryError,
    onRetryHistory: () => refetchNoteHistory(),
    onDeleteNote: handleDeleteNote,
    noteText,
    onNoteTextChange: setNoteText,
    onAddNote: handleAddNote,
    isSending,
    onClose: handleClose
  };

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onClose={handleClose}
        onOpen={() => {}}
        disableSwipeToOpen
        disableDiscovery
        swipeAreaWidth={0}
        ModalProps={{ keepMounted: false }}
        slotProps={{
          paper: {
            sx: {
              height: "85vh",
              borderTopLeftRadius: `calc(${theme.shape.borderRadius} * 2)`,
              borderTopRightRadius: `calc(${theme.shape.borderRadius} * 2)`,
              display: "flex",
              flexDirection: "column"
            }
          }
        }}
      >
        <NoteModalContent variant="sheet" {...contentProps} />
      </SwipeableDrawer>
    );
  }

  return (
    <ModalWrapper
      id="addEditNoteModal"
      open={isOpen}
      onClose={handleClose}
      title={`${t("dealer.auctionDetailsPage.notes") ?? ""}${
        regNo ? ` ${regNo}` : ""
      }`}
      size="sm"
      paperSx={{ maxHeight: "80vh" }}
      sx={{
        "& .MuiDialogContent-root": {
          p: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0
        }
      }}
    >
      <NoteModalContent variant="dialog" {...contentProps} />
    </ModalWrapper>
  );
};

export default NoteModal;
